import type {
  BulkFeedingReportOpResult,
  TrashedFeedingReport,
} from "@strtmn/sds/types"
import { Cache } from "aws-amplify/utils"
import type { LoaderFunction } from "react-router-dom"
import { isDev, isProd } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { getCacheKey, getFromApi, postToApi } from "./utils"

// 10 / 100 minutes
const EXPIRES_IN_MS = (isDev ? 100 : 10) * 60 * 1000

export const getFeedingReport = async (
  orgId: string,
  feedingReportId: string
): Promise<any | null> => {
  console.log("> getFeedingReport")
  const apiName = "frontendApi"
  const feedingReportIdEscaped = feedingReportId.replace("#", "||")
  const path = `/feeding-reports/${orgId}/${feedingReportIdEscaped}`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return null
  }

  try {
    // !isProd && console.debug("try to get item from cache")
    // console.warn("remove item from cache") // DBG
    // await Cache.removeItem(cacheKey) // DBG
    let data = await Cache.getItem(cacheKey)
    if (data) {
      console.log("getFeedingReport: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "getFeedingReport:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      data = (await getFromApi(apiName, path)).json
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("getFeedingReport: Fetched from api:", data)
    }
    // !isProd && console.debug("✅ getFeedingReport: data")
    return await data
  } catch (err) {
    console.error(
      "getFeedingReport: Error fetching FeedingReport data from cache (unlikely) or api (likely)."
    )
    !isProd && console.debug("getFeedingReport: api name =", apiName)
    console.log("getFeedingReport: Error:", err)
    // !isProd && console.debug("❌ getFeedingReport: throw")
    throw err
  }
}

/**
 * Soft-deletes feeding reports (moves them into the org's 7-day-restorable bin).
 * Per-item best-effort: returns a status per requested id rather than throwing
 * on a partial failure, so the caller can tell which ones actually moved.
 *
 * Only reports that actually moved (status "deleted") have their detail cache
 * invalidated - one that failed still lives at its original key, so its
 * cached copy (if any) is still valid.
 */
export const deleteFeedingReports = async (
  orgId: string,
  feedingReportIds: string[]
): Promise<BulkFeedingReportOpResult[]> => {
  console.log("> deleteFeedingReports", feedingReportIds)
  const apiName = "frontendApi"
  const path = `/feeding-reports/${orgId}/bin`
  const { results } = (await postToApi(apiName, path, { feedingReportIds }))
    .json as { results: BulkFeedingReportOpResult[] }

  await Promise.all(
    results
      .filter((result) => result.status === "deleted")
      .map((result) =>
        Cache.removeItem(
          getCacheKey(
            apiName,
            `/feeding-reports/${orgId}/${result.feedingReportId.replace("#", "||")}`
          )
        )
      )
  )

  return results
}

/**
 * Restores previously soft-deleted feeding reports, if still within the restore window.
 * Per-item best-effort, same as `deleteFeedingReports`.
 *
 * No cache invalidation needed here: a 404 (deleted report) is never cached
 * by `getFeedingReport` below, and the one positive cache entry that could
 * exist was already invalidated when the report was deleted in the first
 * place - restoring it doesn't reintroduce a stale value.
 */
export const restoreFeedingReports = async (
  orgId: string,
  feedingReportIds: string[]
): Promise<BulkFeedingReportOpResult[]> => {
  console.log("> restoreFeedingReports", feedingReportIds)
  const apiName = "frontendApi"
  const path = `/feeding-reports/${orgId}/bin/restore`
  const { results } = (await postToApi(apiName, path, { feedingReportIds }))
    .json as { results: BulkFeedingReportOpResult[] }
  return results
}

/**
 * Lists an org's soft-deleted feeding reports that are still within the 7-day restore window.
 */
export const listFeedingReportsBin = async (
  orgId: string
): Promise<TrashedFeedingReport[]> => {
  console.log("> listFeedingReportsBin")
  const apiName = "frontendApi"
  const path = `/feeding-reports/${orgId}/bin`

  if (!(await isUserSignedIn())) {
    return []
  }

  const data = (await getFromApi(apiName, path)).json
  return (data as TrashedFeedingReport[]) ?? []
}

const loadFeedingReport = async (
  orgId: string,
  feedingReportId: string
): Promise<any | undefined> => {
  console.log("> loadFeedingReport")
  const feedingReport = await getFeedingReport(orgId, feedingReportId)
  if (!feedingReport) {
    console.error("loadFeedingReport: got no FeedingReport data")
    return undefined
  }
  // console.log("🎉 got FeedingReport data")
  !isProd && console.log("< loadFeedingReport")
  return feedingReport
}

export const loader: LoaderFunction = async ({
  params,
}): Promise<any | null> => {
  console.log("> FeedingReportsApi.loader")
  if (!params["orgId"]) {
    console.error("FeedingReportsApi.loader: param orgId is missing")
    return null
  }
  const orgId = params["orgId"]
  if (!params["feedingReportId"]) {
    console.error("FeedingReportsApi.loader: param feedingReportId is missing")
    return null
  }
  const feedingReportId = params["feedingReportId"]
  !isProd && console.debug("feedingReportId = ", feedingReportId)
  !isProd && console.debug("orgId = ", orgId)

  const feedingReport = await loadFeedingReport(orgId, feedingReportId)
  if (!feedingReport) {
    return null
  }
  return feedingReport
}
